import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Chip,
  Stack,
  Tooltip,
  Paper,
  Divider,
} from '@mui/material';
import {
  Add,
  Delete,
  ContentCopy,
  Visibility,
  VisibilityOff,
  Edit,
  VpnKey,
} from '@mui/icons-material';
import { useApiKey } from '../contexts/ApiKeyContext';
import { useOrgFeatureFlags } from '../hooks/useOrgFeatureFlags';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { useIsMobile } from '../hooks/useIsMobile';
import {
  ConfirmDialog,
  DataTableShell,
  EmptyState,
  FormDialog,
  InlineMessage,
  LoadingState,
  PageHeader,
  StatusChip,
  PageContainer,
} from '../components/ui';
import {
  ApiKeyPermissionPicker,
  DEFAULT_STORE_API_KEY_PERMISSIONS,
  getPermissionsChipTooltip,
} from '../components/ApiKeyPermissionPicker';
import { ApiKey, apiService } from '../services/api';
import { ApiKeyDataScopePicker } from '../components/ApiKeyDataScopePicker';
import { ApiKeyDataScope, ApiKeyScopeOptions, describeKeyDataScope } from '../types/apiKeyDataScope';
import { formatFirestoreDateOnly, parseFirestoreTimestamp } from '../utils/firestoreDate';
import { CatalogWebhooksCard } from '../components/CatalogWebhooksCard';
import { useToast } from '../contexts/ToastContext';
import { tokens } from '../theme/tokens';

/** Small uppercase faint label above a mobile card's value, matching the ResponsiveRows mobile cell label. */
const mobileLabelSx = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase' as const,
  letterSpacing: '.06em',
  color: tokens.ink.faint,
  display: 'block',
  mb: '4px',
};

/** A key idle (or never used since creation) for longer than this gets a caution hint. */
const STALE_KEY_DAYS = 90;
const DAY_MS = 24 * 60 * 60 * 1000;

const NO_ACCESS_TOOLTIP =
  "This key has no endpoint permissions, so it can't call any endpoint. Edit the key to grant access.";
const STALE_KEY_TOOLTIP =
  "This key hasn't been used in over 90 days (or has never been used and was created over 90 days ago). Consider deleting it if you no longer need it. Keys are not revoked automatically.";

/** Last use, or creation when the key was never used, is older than the threshold. */
const isStaleKey = (key: ApiKey): boolean => {
  const lastActivity = parseFirestoreTimestamp(key.lastUsed) ?? parseFirestoreTimestamp(key.createdAt);
  return lastActivity !== null && Date.now() - lastActivity.getTime() > STALE_KEY_DAYS * DAY_MS;
};

/**
 * Status chip. An active key with no permissions is not really "Active": it
 * cannot call anything, so it reads as a caution with an explanation.
 */
const KeyStatusChip: React.FC<{ keyRecord: ApiKey }> = ({ keyRecord }) => {
  if (!keyRecord.isActive) {
    return <StatusChip label="Inactive" tone="default" size="small" />;
  }
  if ((keyRecord.permissions ?? []).length === 0) {
    return (
      <Tooltip title={NO_ACCESS_TOOLTIP}>
        <Box component="span" tabIndex={0} sx={{ display: 'inline-flex' }}>
          <StatusChip label="Active · no access" tone="caution" size="small" />
        </Box>
      </Tooltip>
    );
  }
  return <StatusChip label="Active" tone="success" size="small" />;
};

/** Display-only hint; nothing is revoked automatically. */
const StaleKeyHint: React.FC<{ keyRecord: ApiKey }> = ({ keyRecord }) => {
  if (!isStaleKey(keyRecord)) return null;
  return (
    <Tooltip title={STALE_KEY_TOOLTIP}>
      <Box component="span" tabIndex={0} sx={{ display: 'inline-flex' }}>
        <StatusChip label="Not used in 90+ days" tone="caution" size="small" />
      </Box>
    </Tooltip>
  );
};

export const ApiKeysPage: React.FC = () => {
  const { isOnUnlessOff: isUiSwitchOn } = useOrgFeatureFlags();
  const { isAdmin } = useAuth();
  const { isImpersonating } = useImpersonation();
  const isMobile = useIsMobile();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ApiKey | null>(null);
  const [newKeyName, setNewKeyName] = useState('');
  const [editKeyName, setEditKeyName] = useState('');
  const [createPermissions, setCreatePermissions] = useState<string[]>([
    ...DEFAULT_STORE_API_KEY_PERMISSIONS,
  ]);
  const [editPermissions, setEditPermissions] = useState<string[]>([
    ...DEFAULT_STORE_API_KEY_PERMISSIONS,
  ]);
  // Backend flag `scoped_api_keys`: the picker renders only when the options say it is on.
  const [scopeOptions, setScopeOptions] = useState<ApiKeyScopeOptions | null>(null);
  const [createScope, setCreateScope] = useState<ApiKeyDataScope | null>(null);
  const [editScope, setEditScope] = useState<ApiKeyDataScope | null>(null);
  useEffect(() => {
    let cancelled = false;
    apiService
      .getApiKeyScopeOptions()
      .then((options) => {
        if (!cancelled) setScopeOptions(options);
      })
      .catch(() => {
        if (!cancelled) setScopeOptions(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const { showToast } = useToast();
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set());
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { 
    apiKeys, 
    isLoading, 
    error: contextError,
    createApiKey: createKey,
    updateApiKey: updateKey,
    deleteApiKey: deleteKey,
  } = useApiKey();

  // A platform admin skips the switch, except while viewing as an account: then they see what it sees.
  if (!(isAdmin && !isImpersonating) && !isUiSwitchOn('ui_api_access')) {
    return (
      <PageContainer>
        <InlineMessage severity="info">
          API access is currently disabled for this account.
        </InlineMessage>
      </PageContainer>
    );
  }

  const showMessage = (message: string, severity: 'success' | 'error' = 'success') => showToast(message, severity);

  const openCreateDialog = () => {
    setNewKeyName('');
    setCreatePermissions([...DEFAULT_STORE_API_KEY_PERMISSIONS]);
    setCreateScope(null);
    setDialogOpen(true);
  };

  const openEditDialog = (key: ApiKey) => {
    setEditTarget(key);
    setEditKeyName(key.name || '');
    setEditPermissions(
      key.permissions && key.permissions.length > 0
        ? [...key.permissions]
        : [...DEFAULT_STORE_API_KEY_PERMISSIONS]
    );
    setEditScope(key.dataScope || null);
  };

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) return;
    if (createPermissions.length === 0) {
      showMessage('Select at least one permission.', 'error');
      return;
    }
    try {
      await createKey(newKeyName.trim(), createPermissions, scopeOptions?.enabled ? createScope : null);
      setDialogOpen(false);
      setNewKeyName('');
      setCreatePermissions([...DEFAULT_STORE_API_KEY_PERMISSIONS]);
      showMessage('API key created successfully!');
    } catch (error) {
      showMessage(
        `Failed to create API key: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'error'
      );
    }
  };

  const handleUpdateKey = async () => {
    if (!editTarget) return;
    if (!editKeyName.trim()) {
      showMessage('Key name is required.', 'error');
      return;
    }
    if (editPermissions.length === 0) {
      showMessage('Select at least one permission.', 'error');
      return;
    }
    try {
      await updateKey(editTarget.id, {
        name: editKeyName.trim(),
        permissions: editPermissions,
        // null clears the scope (the key inherits the organization again); omitted while the flag is off.
        ...(scopeOptions?.enabled ? { dataScope: editScope } : {}),
      });
      setEditTarget(null);
      showMessage('API key permissions updated.');
    } catch (error) {
      showMessage(
        `Failed to update API key: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'error'
      );
    }
  };

  const handleDeleteKey = (id: string) => {
    setDeleteTargetId(id);
  };

  const handleConfirmDeleteKey = async () => {
    if (!deleteTargetId) return;
    setIsDeleting(true);
    try {
      await deleteKey(deleteTargetId);
      showMessage('API key deleted successfully!');
    } catch (error) {
      showMessage(
        `Failed to delete API key: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'error'
      );
    } finally {
      setIsDeleting(false);
      setDeleteTargetId(null);
    }
  };

  const handleCopyKey = (key: string) => {
    navigator.clipboard.writeText(key);
    showMessage('API key copied to clipboard!');
  };

  const toggleKeyVisibility = (id: string) => {
    const newVisibleKeys = new Set(visibleKeys);
    if (newVisibleKeys.has(id)) {
      newVisibleKeys.delete(id);
    } else {
      newVisibleKeys.add(id);
    }
    setVisibleKeys(newVisibleKeys);
  };

  const maskApiKey = (key: string) => {
    return key.substring(0, 8) + '•'.repeat(key.length - 12) + key.substring(key.length - 4);
  };

  if (contextError) {
    return (
      <PageContainer>
        <InlineMessage severity="error">Failed to load API keys. Please try again later.</InlineMessage>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="API Keys"
        description="Manage API keys and endpoint permissions for Product Data API access."
        actionLabel="Create New Key"
        actionIcon={<Add />}
        onAction={openCreateDialog}
      />

      {isLoading ? (
        <LoadingState message="Loading API keys..." />
      ) : !apiKeys || apiKeys.length === 0 ? (
        <EmptyState
          icon={<VpnKey />}
          title="No API keys yet"
          description="Create a key to call the Product Data API from your own tools and integrations."
          actionLabel="Create New Key"
          onAction={openCreateDialog}
        />
      ) : isMobile ? (
        <Paper sx={{ borderRadius: 2 }}>
          <Stack divider={<Divider />}>
            {apiKeys.map((key) => {
              const permissions = key.permissions || [];
              return (
                <Box key={key.id} sx={{ p: 2 }}>
                  <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                    <Typography variant="subtitle2" sx={{ minWidth: 0, wordBreak: 'break-word' }}>{key.name}</Typography>
                    <KeyStatusChip keyRecord={key} />
                  </Stack>

                  <Box sx={{ mt: 1 }}>
                    <Typography component="span" sx={mobileLabelSx}>API Key</Typography>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                        {visibleKeys.has(key.id) ? key.key : maskApiKey(key.key)}
                      </Typography>
                      <IconButton size="small" onClick={() => toggleKeyVisibility(key.id)}>
                        {visibleKeys.has(key.id) ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                      <IconButton size="small" onClick={() => handleCopyKey(key.key)}>
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  <Box sx={{ mt: 1 }}>
                    <Typography component="span" sx={mobileLabelSx}>Permissions</Typography>
                    {permissions.length === 0 ? (
                      <Tooltip title={getPermissionsChipTooltip(permissions)}>
                        <Chip label="None (blocked)" size="small" color="warning" />
                      </Tooltip>
                    ) : permissions.includes('*') ? (
                      <Tooltip title={getPermissionsChipTooltip(permissions)}>
                        <Chip label="Full access" size="small" color="warning" />
                      </Tooltip>
                    ) : (
                      <Tooltip title={getPermissionsChipTooltip(permissions)}>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {permissions.slice(0, 3).map((p) => (
                            <Chip key={p} label={p} size="small" variant="outlined" />
                          ))}
                          {permissions.length > 3 && (
                            <Chip label={`+${permissions.length - 3}`} size="small" />
                          )}
                        </Stack>
                      </Tooltip>
                    )}
                  </Box>

                  {scopeOptions?.enabled && (
                    <Box sx={{ mt: 1 }}>
                      <Typography component="span" sx={mobileLabelSx}>Data scope</Typography>
                      <Typography variant="body2">{describeKeyDataScope(key.dataScope)}</Typography>
                    </Box>
                  )}

                  <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                    <Box>
                      <Typography component="span" sx={mobileLabelSx}>Created</Typography>
                      <Typography variant="body2">{formatFirestoreDateOnly(key.createdAt)}</Typography>
                    </Box>
                    <Box>
                      <Typography component="span" sx={mobileLabelSx}>Last Used</Typography>
                      <Typography variant="body2">
                        {key.lastUsed ? formatFirestoreDateOnly(key.lastUsed) : 'Never'}
                      </Typography>
                    </Box>
                  </Stack>
                  {isStaleKey(key) && (
                    <Box sx={{ mt: 1 }}>
                      <StaleKeyHint keyRecord={key} />
                    </Box>
                  )}

                  <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => openEditDialog(key)}
                      disabled={isLoading}
                      aria-label="Edit permissions"
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteKey(key.id)}
                      disabled={isLoading}
                      aria-label="Delete key"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </Paper>
      ) : (
        <DataTableShell>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>API Key</TableCell>
                  <TableCell>Permissions</TableCell>
                  {scopeOptions?.enabled && <TableCell>Data scope</TableCell>}
                  <TableCell>Created</TableCell>
                  <TableCell>Last Used</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {apiKeys.map((key) => {
                  const permissions = key.permissions || [];
                  return (
                  <TableRow key={key.id}>
                    {/* nowrap: the key column has spare width, so the name should not break ("tracey / test"). */}
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{key.name}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" fontFamily="monospace">
                          {visibleKeys.has(key.id) ? key.key : maskApiKey(key.key)}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => toggleKeyVisibility(key.id)}
                        >
                          {visibleKeys.has(key.id) ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleCopyKey(key.key)}
                        >
                          <ContentCopy />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>
                      {permissions.length === 0 ? (
                        <Tooltip title={getPermissionsChipTooltip(permissions)}>
                          <Chip label="None (blocked)" size="small" color="warning" />
                        </Tooltip>
                      ) : permissions.includes('*') ? (
                        <Tooltip title={getPermissionsChipTooltip(permissions)}>
                          <Chip label="Full access" size="small" color="warning" />
                        </Tooltip>
                      ) : (
                        <Tooltip title={getPermissionsChipTooltip(permissions)}>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {permissions.slice(0, 3).map((p) => (
                              <Chip key={p} label={p} size="small" variant="outlined" />
                            ))}
                            {permissions.length > 3 && (
                              <Chip label={`+${permissions.length - 3}`} size="small" />
                            )}
                          </Stack>
                        </Tooltip>
                      )}
                    </TableCell>
                    {scopeOptions?.enabled && (
                      <TableCell sx={{ maxWidth: 240 }}>{describeKeyDataScope(key.dataScope)}</TableCell>
                    )}
                    <TableCell>
                      {formatFirestoreDateOnly(key.createdAt)}
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5} alignItems="flex-start">
                        <span>
                          {key.lastUsed
                            ? formatFirestoreDateOnly(key.lastUsed)
                            : 'Never'}
                        </span>
                        <StaleKeyHint keyRecord={key} />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <KeyStatusChip keyRecord={key} />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        color="primary"
                        onClick={() => openEditDialog(key)}
                        disabled={isLoading}
                        aria-label="Edit permissions"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteKey(key.id)}
                        disabled={isLoading}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DataTableShell>
      )}

      {/* Create Key Dialog */}
      <FormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleCreateKey}
        title="Create New API Key"
        submitLabel={isLoading ? 'Creating...' : 'Create Key'}
        submitDisabled={!newKeyName.trim() || createPermissions.length === 0 || isLoading}
        maxWidth="md"
      >
        <TextField
          autoFocus
          margin="dense"
          label="Key Name"
          fullWidth
          variant="outlined"
          value={newKeyName}
          onChange={(e) => setNewKeyName(e.target.value)}
          placeholder="e.g., Impulse metadata writer"
          sx={{ mb: 2, mt: 1 }}
        />
        <ApiKeyPermissionPicker
          value={createPermissions}
          onChange={setCreatePermissions}
          disabled={isLoading}
        />
        <ApiKeyDataScopePicker
          value={createScope}
          onChange={setCreateScope}
          options={scopeOptions}
          disabled={isLoading}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Copy and store the key securely after creation. Permissions can be changed later
          without rotating the key.
        </Typography>
      </FormDialog>

      <CatalogWebhooksCard />

      {/* Edit permissions dialog */}
      <FormDialog
        open={editTarget !== null}
        onClose={() => setEditTarget(null)}
        onSubmit={handleUpdateKey}
        title="Edit API Key Permissions"
        submitLabel={isLoading ? 'Saving...' : 'Save'}
        submitDisabled={!editKeyName.trim() || editPermissions.length === 0 || isLoading}
        maxWidth="md"
      >
        <TextField
          autoFocus
          margin="dense"
          label="Key Name"
          fullWidth
          variant="outlined"
          value={editKeyName}
          onChange={(e) => setEditKeyName(e.target.value)}
          sx={{ mb: 2, mt: 1 }}
        />
        <ApiKeyPermissionPicker
          value={editPermissions}
          onChange={setEditPermissions}
          disabled={isLoading}
        />
        <ApiKeyDataScopePicker
          value={editScope}
          onChange={setEditScope}
          options={scopeOptions}
          disabled={isLoading}
        />
      </FormDialog>

      {/* Delete key confirmation */}
      <ConfirmDialog
        open={deleteTargetId !== null}
        title="Delete API Key"
        message="Are you sure you want to delete this API key? Applications using it will lose access immediately."
        confirmLabel="Delete"
        confirmColor="error"
        loading={isDeleting}
        onConfirm={handleConfirmDeleteKey}
        onClose={() => setDeleteTargetId(null)}
      />

    </PageContainer>
  );
};
